/**
 * Live MCP server list from the platform registry (#939, Feature D).
 *
 * The registry is read straight from the browser: it needs no sign-in and allows
 * cross-site reads (`Access-Control-Allow-Origin: *`), so there is no scheduled job or
 * stored file and the catalog is always current. This module shapes the raw registry
 * entries into what the MCP pages show:
 *   - non-prod duplicates (`*-np`) are dropped (until #810 lets them be marked private);
 *   - only active, latest versions are kept;
 *   - the endpoint people copy uses the `mcp.ai` host, not the canonical `mcp-prod.ai`.
 * If the registry can't be reached, the last list this browser loaded is returned
 * instead (flagged as cached), so the catalog never shows an empty list as if there
 * were no servers.
 */

import { reportError } from "./newrelic";

export const REGISTRY_URL = "https://registry.mcp-prod.ai.coxautoinc.com/v0.1/servers";

/** Host the registry publishes endpoints under (canonical; for automation). */
export const CANONICAL_HOST = "mcp-prod.ai.coxautoinc.com";
/** Host people copy into their clients (vanity alias of the same platform). */
export const PEOPLE_FACING_HOST = "mcp.ai.coxautoinc.com";

const PAGE_LIMIT = 100;
/** Stops a registry that keeps returning a cursor from looping forever. */
const MAX_PAGES = 20;
const DEFAULT_TIMEOUT_MS = 10_000;
const CACHE_KEY = "mcp-registry:last-good:v1";
const OFFICIAL_META_KEY = "io.modelcontextprotocol.registry/official";

export interface McpServer {
  /** Short name, e.g. "rally" (the registry's "mcp-services/rally" without the namespace). */
  serverName: string;
  title: string;
  description: string;
  version: string;
  /** People-facing endpoint, e.g. https://mcp.ai.coxautoinc.com/rally/mcp */
  url: string;
  /** ISO timestamp the server was first published ("date added"). */
  publishedAt: string;
}

export interface McpServersResult {
  servers: McpServer[];
  /** "cached" means the registry couldn't be reached and this is the last list this browser loaded. */
  source: "live" | "cached";
  /** When this list was fetched from the registry. */
  fetchedAt: string;
}

// The registry's response is read as `unknown` and checked field by field (classifyEntry), so
// an entry of the wrong shape is dropped on its own instead of throwing for the whole list.

/**
 * Whether a short server name is safe to build copy-paste snippets from. The name goes
 * unquoted into a shell command (`claude mcp add ... {name} {url}`) and into a `.mcp.json`
 * key, so anything beyond letters, digits, `.`, `_` and `-` is refused rather than escaped.
 */
export function isSafeServerName(name: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(name);
}

/**
 * The people-facing endpoint for a registry URL, or null when it isn't one we'd hand people to
 * paste: it must be https with a plain host and a path of URL-safe characters, and no
 * credentials, port, query or fragment. Like the name, it goes unquoted into a shell command.
 */
export function safeEndpoint(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" || parsed.username || parsed.password || parsed.port) return null;
  if (parsed.search || parsed.hash || url.includes("?") || url.includes("#")) return null;
  if (!/^[A-Za-z0-9.-]+$/.test(parsed.hostname) || !/^\/[A-Za-z0-9._~/-]*$/.test(parsed.pathname)) return null;
  return toPeopleFacingUrl(parsed.toString());
}

function isSafeServer(server: McpServer): boolean {
  return isSafeServerName(server.serverName) && safeEndpoint(server.url) === server.url;
}

/** Swaps the canonical host for the people-facing one; anything else is returned unchanged. */
export function toPeopleFacingUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === CANONICAL_HOST) parsed.hostname = PEOPLE_FACING_HOST;
    return parsed.toString();
  } catch {
    return url;
  }
}

function isNonProd(serverName: string): boolean {
  return serverName.endsWith("-np");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Why an entry isn't listed when it's not one we drop on purpose (-np duplicates, inactive or
 * superseded versions). These are reported, since they mean the registry sent something we
 * don't understand, and a rename on its side (of `streamable-http`, say) would otherwise make
 * servers vanish without anyone noticing.
 */
type DropReason = "malformed" | "no-name" | "unsafe-name" | "no-http-remote" | "unsafe-endpoint";

type Classified =
  | { kind: "listed"; server: McpServer; registryName: string }
  | { kind: "skipped" }
  | { kind: "dropped"; reason: DropReason };

const HTTP_REMOTE_TYPES = new Set(["streamable-http", "http"]);

/** Sorts one raw registry entry into listed, skipped on purpose, or dropped. Never throws. */
function classifyEntry(entry: unknown): Classified {
  if (!isRecord(entry) || !isRecord(entry.server)) return { kind: "dropped", reason: "malformed" };
  const server = entry.server;

  const meta = isRecord(entry._meta) ? entry._meta[OFFICIAL_META_KEY] : undefined;
  const official = isRecord(meta) ? meta : {};
  if (typeof official.status === "string" && official.status !== "active") return { kind: "skipped" };
  if (official.isLatest === false) return { kind: "skipped" };

  const registryName = optionalString(server.name);
  if (!registryName) return { kind: "dropped", reason: "no-name" };
  const serverName = registryName.split("/").pop() ?? "";
  if (isNonProd(serverName)) return { kind: "skipped" };
  if (!isSafeServerName(serverName)) return { kind: "dropped", reason: "unsafe-name" };

  const remotes = Array.isArray(server.remotes) ? server.remotes : [];
  const remote = remotes.find(
    (r): r is { type: string; url: string } =>
      isRecord(r) && typeof r.type === "string" && HTTP_REMOTE_TYPES.has(r.type) && typeof r.url === "string" && r.url !== "",
  );
  if (!remote) return { kind: "dropped", reason: "no-http-remote" };
  const url = safeEndpoint(remote.url);
  if (!url) return { kind: "dropped", reason: "unsafe-endpoint" };

  return {
    kind: "listed",
    registryName,
    server: {
      serverName,
      title: optionalString(server.title).trim() || serverName,
      description: optionalString(server.description).trim(),
      version: optionalString(server.version),
      url,
      publishedAt: optionalString(official.publishedAt),
    },
  };
}

/** Shapes one registry entry, or returns null for anything the catalog shouldn't list. */
export function normalizeEntry(entry: unknown): McpServer | null {
  const result = classifyEntry(entry);
  return result.kind === "listed" ? result.server : null;
}

/**
 * Shapes a full registry listing: filtered, de-duplicated by server name, sorted by title.
 * One bad entry is dropped on its own rather than failing the list, and the drops are
 * reported once per load with a count per reason. The short name is the page's URL and the
 * CLI name, so it must be unique. Every server is in one namespace today (mcp-services); if
 * two namespaces ever publish the same short name, the first one listed wins and the clash is
 * reported.
 */
export function normalizeServers(entries: unknown[]): McpServer[] {
  const byName = new Map<string, { server: McpServer; registryName: string }>();
  const dropped = new Map<DropReason, number>();
  for (const entry of entries) {
    const result = classifyEntry(entry);
    if (result.kind === "dropped") dropped.set(result.reason, (dropped.get(result.reason) ?? 0) + 1);
    if (result.kind !== "listed") continue;
    const { server, registryName } = result;
    const seen = byName.get(server.serverName);
    if (!seen) byName.set(server.serverName, { server, registryName });
    else if (seen.registryName !== registryName) {
      reportError(
        new Error(`MCP registry: ${registryName} and ${seen.registryName} share the name "${server.serverName}"`),
        "mcp-registry-name-clash",
      );
    }
  }
  if (dropped.size > 0) {
    const total = [...dropped.values()].reduce((a, b) => a + b, 0);
    const reasons = [...dropped.entries()].map(([reason, n]) => `${reason}:${n}`).join(",");
    reportError(new Error(`MCP registry: dropped ${total} entries (${reasons})`), "mcp-registry-dropped", {
      dropped: total,
      reasons,
    });
  }
  return [...byName.values()].map((v) => v.server).sort((a, b) => a.title.localeCompare(b.title));
}

interface FetchOptions {
  fetchImpl?: typeof fetch;
  registryUrl?: string;
  timeoutMs?: number;
}

/** Reads every page of the registry. Throws if any page fails, so a partial list is never returned. */
export async function fetchRegistryServers({
  fetchImpl = fetch,
  registryUrl = REGISTRY_URL,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: FetchOptions = {}): Promise<McpServer[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const entries: unknown[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_PAGES; page++) {
      const url = new URL(registryUrl);
      url.searchParams.set("limit", String(PAGE_LIMIT));
      if (cursor) url.searchParams.set("cursor", cursor);

      const response = await fetchImpl(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(`MCP registry returned ${response.status}`);
      const body: unknown = await response.json();
      if (!isRecord(body)) throw new Error("MCP registry returned a page that isn't an object");
      // A page without `servers` adds nothing; one whose `servers` isn't a list is a contract
      // change, so it fails the load (and is reported) rather than reading as no servers.
      if (body.servers !== undefined && !Array.isArray(body.servers)) {
        throw new Error("MCP registry returned a page whose servers isn't a list");
      }
      entries.push(...((body.servers as unknown[] | undefined) ?? []));

      const next = isRecord(body.metadata) ? body.metadata.nextCursor : undefined;
      cursor = typeof next === "string" && next ? next : undefined;
      if (!cursor) return normalizeServers(entries);
    }
    throw new Error(`MCP registry returned more than ${MAX_PAGES} pages`);
  } finally {
    clearTimeout(timer);
  }
}

function readCache(): McpServersResult | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as { servers?: McpServer[]; fetchedAt?: string };
    if (!Array.isArray(cached.servers) || !cached.fetchedAt) return null;
    // Checked again on the way out: anything can be written to localStorage, and these feed
    // the same copy-paste commands as a live list.
    const servers = cached.servers.filter(
      (s) => typeof s?.serverName === "string" && typeof s.url === "string" && isSafeServer(s),
    );
    if (servers.length === 0) return null;
    return { servers, source: "cached", fetchedAt: cached.fetchedAt };
  } catch {
    return null;
  }
}

function writeCache(servers: McpServer[], fetchedAt: string): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ servers, fetchedAt }));
  } catch {
    // Storage can be full, blocked or unavailable; the live list still works without it.
  }
}

/**
 * Loads the server list: live from the registry, falling back to the last good list this
 * browser loaded. Throws only when the registry fails and there is no cached list. An
 * empty live list is treated as a failure, since the platform always has servers.
 */
export async function loadMcpServers(options: FetchOptions = {}): Promise<McpServersResult> {
  try {
    const servers = await fetchRegistryServers(options);
    if (servers.length === 0) throw new Error("MCP registry returned no servers");
    const fetchedAt = new Date().toISOString();
    writeCache(servers, fetchedAt);
    return { servers, source: "live", fetchedAt };
  } catch (err) {
    const cached = readCache();
    if (cached) {
      // Reported here because the caller only sees a successful (cached) result, so an outage
      // would otherwise go unnoticed for as long as browsers have a list stored.
      reportError(err, "mcp-registry-fetch", { servedFromCache: true });
      return cached;
    }
    throw err;
  }
}
